import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import {
  SectionContainerBase,
  TwoColumnSectionBase,
  FooterBase,
  NavigationBase,
  PickerSelectSimple,
  SidebarBase,
  ButtonBase,
} from "@reusejs/react";
import NavButtons from "./NavButtons";
import {
  HomeIcon,
  FireIcon,
  UserGroupIcon,
  UserIcon,
  DocumentTextIcon,
  ArchiveIcon,
  LogoutIcon,
  CalendarIcon,
} from "@heroicons/react/outline";

import { getTeams, getBankBranchesByid } from "services";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentBranch, clearBankerAuth } from "store";
import Logo from "../header/Logo";
import BankerSidebar from "./bankerSidebar";

export default function BankerDashboardLayout(props: any) {
  const router = useRouter();
  const dispatch = useDispatch();

  const userAttributes = useSelector((state: any) => {
    return state.bankerAuth?.user?.attributes;
  });

  const [navigation, setNavigation] = useState([
    {
      label: "Home",
      href: "/",
      icon: HomeIcon,
      current: false,
    },
    {
      label: "Auction Events",
      href: "/dashboard/events",
      icon: FireIcon,
      current: false,
    },
    {
      label: "Reports",
      href: "/dashboard/reports",
      icon: DocumentTextIcon,
      current: false,
    },
    {
      label: "Archives",
      href: "/dashboard/archives",
      icon: ArchiveIcon,
      current: false,
    },
    {
      label: "Teams",
      href: "/dashboard/teams",
      icon: UserGroupIcon,
      current: false,
    },
    {
      label: "Profile",
      href: "/dashboard/profile",
      icon: UserIcon,
      current: false,
    },
    {
      label: "Event Calendar",
      href: "/dashboard/event-calendar",
      icon: CalendarIcon,
      current: false,
    },
  ]);

  useEffect(() => {
    setNavigation((prev: any) => {
      let next = prev.map((element: any) => {
        if (element.children) {
          let children = element.children.map((child: any) => {
            child.current = child.href === router.asPath ? true : false;
            return child;
          });
          element.children = children;
        }

        element.current = element.href === router.asPath ? true : false;
        return element;
      });

      return next;
    });
  }, [router]);

  const handleLogout = () => {
    dispatch(clearBankerAuth());
    router.push("/login");
  };

  return (
    <div className="min-h-screen">
      <SectionContainerBase
        sectionContainerBaseClasses={{
          outerContainerStyles: "bg-white overflow-hidden",
          innerContainerStyles: "relative",
        }}
      >
        <TwoColumnSectionBase
          firstColumnEl={
            <>
              <SidebarBase
                navigationItems={navigation}
                topItem={<Logo />}
                onClick={(e: any, item: any) =>
                  router.push(item.href, undefined, { shallow: true })
                }
              />
              <div className="border-t h-18 w-64 flex flex-col px-6 py-2 items-center absolute bottom-0 left-0">
                <div className="text-gray-400 mb-2">
                  {userAttributes && userAttributes[0].value}
                </div>
                <ButtonBase
                  variant="primary"
                  type="submit"
                  label="Logout"
                  onClick={() => handleLogout()}
                  buttonPrefix={
                    <LogoutIcon
                      height="16"
                      width="16"
                      className="rotate-180 mr-2"
                    />
                  }
                  buttonBaseClasses={{
                    font: "text-sm font-medium",
                    backgroundColor: "bg-blue-600 hover:bg-blue-700",
                    textColor: "text-white",
                    border: "any",
                    width: "sm:w-full",
                    padding: "px-2 py-2",
                  }}
                />
              </div>
            </>
          }
          secondColumnEl={
            <div className="flex flex-col">
              <NavigationBase
                left={LeftItem}
                // right={NavButtons} // removed from right top corner
                sidebar={BankerSidebar}
                backgroundClasses="bg-white fixed top-0 z-20 left-0 sm:left-64 right-0 dark:bg-[#12263f] "
                widthClasses="mx-auto w-full px-6"
                leftArrangement="w-full flex justify-between"
                middleArrangement="none"
                rightArrangement="flex-1 justify-end"
                borderClasses="relative border-b border-gray-200 dark:border-blue-800"
              />
              <div className="bg-white pt-16 dark:bg-[#12263f]">
                {props.children}
              </div>
              <FooterBase
                bottom={() => {
                  return (
                    <p className="text-base text-gray-400 xl:text-center">
                      &copy; 2022 Auction Bazaar pvt. ltd. All rights reserved.
                    </p>
                  );
                }}
                backgroundClasses="bg-white dark:bg-[#12263f]"
                widthClasses="none"
                bottomArrangement="py-1 border-t border-gray-200 dark:border-blue-800"
              />
            </div>
          }
          twoColumnSectionBaseClasses={{
            firstElWrapperStyles:
              "hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-green-100",
            secondElWrapperStyles:
              "w-full self-stretch overflow-scroll md:pl-64",
            columnsFlexWrapperStyles:
              "flex items-start justify-between flex-col md:flex-row",
          }}
        />
      </SectionContainerBase>
    </div>
  );
}

function LeftItem(props: any) {
  const router = useRouter();
  const [bankLogo, setBankLogo] = useState(null);
  const { for_branch } = router.query;
  const dispatch = useDispatch();
  const hanldeBranchChange = (value: any) => {
    dispatch(setCurrentBranch(value));
  };

  const bank = useSelector((state: any) => {
    return state.bankerAuth.bank;
  });

  const currentBranch = useSelector((state: any) => {
    return state.bankerAuth.currentBranch;
  });

  useEffect(() => {
    (async () => {
      const bdata: any = await getBankBranches();

      if (for_branch) {
        const filteredData = bdata.filter(
          (item: any) => item.value === for_branch
        );
        hanldeBranchChange(filteredData[0]);
      }
    })();
  }, [router]);

  const getBankBranches = async () => {
    const data: any = await getTeams();
    const branches: any = await Promise.all(
      data.map(async (item: any) => {
        if (item["teams*tenant"] === "bank-branch") {
          const branchIdData: any = await getBankBranchesByid([
            item["teams*name"],
          ]);
          if (branchIdData.length > 0) {
            const branchData = branchIdData[0];
            return { ...branchData, ...{ team: item } };
          }
        }
      })
    );

    const filteredBranches: any = branches.filter((item: any) => item && item);
    const filteredData =
      filteredBranches.length > 0 &&
      filteredBranches.map((item: any) => {
        return { value: item.uuid, label: item.name, team: item.team };
      });
    return filteredData;
  };

  useEffect(() => {
    if (bank?.logo?.key_path) {
      setBankLogo(bank.logo.key_path);
    }
  }, [bank]);

  return (
    <>
      <div className="hidden lg:flex flex-row items-center rounded">
        <PickerSelectSimple
          dataSource={async (c) => {
            return await getBankBranches();
          }}
          defaultString="Select Branch"
          defaultSelected={[currentBranch ? currentBranch : {}]}
          onChange={(value: any) => hanldeBranchChange(value)}
          valueKey="value"
          multiple={false}
          enableClear={false}
          enableClose={false}
          enableSearch={false}
          disabled={false}
          variant="dark"
        />
      </div>
      <NavButtons bankLogo={bankLogo} {...props} />
    </>
  );
}
